import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Cloud, Zap, Waves } from 'lucide-react';

export const MechanismSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const mechanisms = [
    {
      icon: Cloud,
      title: "Le Cloud de potentialités",
      description: "Tu as en toi des ressources qui ne sont pas accessibles en continu : paix, clarté, joie-force, souveraineté, intuition stable. L'École est conçue comme un Cloud : tu te reconnectes à une version plus haute de toi… puis tu l'installes dans la matière."
    },
    {
      icon: Zap,
      title: "Le saut quantique",
      description: "Au lieu de réparer \"morceau par morceau\", tu changes d'état. Et quand ton état change, tes choix changent."
    },
    {
      icon: Waves,
      title: "La signature \"dauphin\"",
      description: "Ici, ce n'est pas un décor : c'est une qualité d'être. Fluidité, cohérence, présence, joie-force, vérité simple."
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Le mécanisme signature
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
            <span className="text-gradient-gold">CLOUD</span> & <span className="text-gradient-gold">TIMELINE</span>
          </h2>
        </motion.div>

        {/* Mechanism Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {mechanisms.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
              className="group"
            >
              <div className="luxury-card rounded-3xl p-8 h-full text-center hover:border-accent/40 transition-all duration-500 hover:-translate-y-2">
                <motion.div
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                >
                  <item.icon className="w-10 h-10 text-accent" />
                </motion.div>
                <h3 className="font-display text-xl text-foreground mb-4">{item.title}</h3>
                <p className="text-muted-foreground font-body leading-relaxed">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Personal Story */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="glass-gold rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Decorative corner accents */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-accent/30 rounded-tl-3xl" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-accent/30 rounded-br-3xl" />
            
            <p className="text-xl md:text-2xl font-display text-foreground leading-relaxed mb-6">
              Ma rencontre avec les Maîtres Dauphins m'a 
              <span className="text-accent"> sauvé la vie</span> et désormais je leur dédie la mienne 
              pour qu'ils te <span className="text-accent">transmettent leur message.</span>
            </p>
            <p className="text-lg text-muted-foreground font-body">
              Je te souhaite de tout cœur de partir à leur rencontre afin de vibrer 
              aux merveilleuses fréquences de la joie et l'amour.
            </p>
            
            {/* Signature decoration */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-px bg-accent/50" />
              <span className="text-accent font-display italic">Gilles</span>
              <div className="w-12 h-px bg-accent/50" />
            </div>
          </div>
        </motion.div>

        {/* Decorative Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-16 flex justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/09/DALL%C2%B7E-2024-09-01-16.45.56-An-ethereal-and-spiritual-illustration-representing-the-Mirror-of-Light-healing-protocol.-A-semi-transparent-human-silhouette-stands-at-the-center-.webp"
              alt="Mirror of Light"
              className="relative rounded-3xl max-w-md w-full border border-accent/20 shadow-gold"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MechanismSection;
